import { Component, Inject } from '@angular/core';
import { Condition, ConditionGroup } from './condition.model';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
	ConditionRelation,
	ConditionType,
	VisibilityConditionDto
} from '../../../../../../api/src/creative-unit-package/models/visibility-condition.dto';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { GlobalService } from '../../../_core/state/global';

@Component({
	selector: 'app-visibility-conditions-dialog',
	templateUrl: './visibility-conditions-dialog.component.html',
	styleUrls: ['./visibility-conditions-dialog.component.css']
})
export class VisibilityConditionsDialogComponent {
	protected conditions: ConditionGroup;
	protected jsonValue = '';
	protected isValid = true;

	constructor(
		public dialogRef: MatDialogRef<VisibilityConditionsDialogComponent>,
		public dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			name: string;
			visibilityConditions: VisibilityConditionDto | VisibilityConditionDto[];
			mergeTagState: { [key: string]: any };
		},
		private globalService: GlobalService
	) {
		this.conditions = this.prepareConditionData(data.visibilityConditions);
		this.isValid = this.validate(this.conditions);
	}

	private prepareConditionData(data: VisibilityConditionDto | VisibilityConditionDto[]) {
		if (!data || (Array.isArray(data) && data.length === 0)) {
			return {
				type: ConditionType.AND,
				conditions: [
					{
						path: '',
						relation: ConditionRelation.EQ,
						value: ''
					}
				]
			};
		}

		if (Array.isArray(data)) {
			if (data.length === 1 && !('path' in data[0])) {
				return this.prepareCondition(data[0]) as ConditionGroup;
			}

			return this.prepareConditionGroup({
				type: ConditionType.AND,
				conditions: data
			});
		}

		return this.prepareCondition(data) as ConditionGroup;
	}

	public setRootCondition(groupCondition: ConditionGroup) {
		this.conditions = groupCondition;
	}

	public onChange() {
		this.isValid = this.validate(this.conditions);
	}

	private prepareCondition(data: VisibilityConditionDto): ConditionGroup | Condition {
		if ('path' in data) {
			return this.prepareConditionItem(data);
		}
		return this.prepareConditionGroup(data);
	}

	private prepareConditionGroup(data: VisibilityConditionDto): ConditionGroup {
		return {
			type: data.type as ConditionType,
			conditions: data.conditions?.length ? data.conditions.map(item => this.prepareCondition(item)) : []
		};
	}

	private prepareValue(value: number | string, relation: ConditionRelation) {
		if ([ConditionRelation.UNDEFINED, ConditionRelation.NOT_UNDEFINED].includes(relation)) {
			return undefined;
		}

		return value;
	}

	private prepareConditionItem(data: VisibilityConditionDto): Condition {
		return {
			path: data.path,
			relation: data.relation,
			value: this.prepareValue(data.value, data.relation)
		};
	}

	// Recursively generate JSON from the ConditionGroup structure
	generateJSONObject(group: ConditionGroup): any {
		console.log('group', group);

		if (!group) {
			return {};
		}

		return {
			type: group.type,
			conditions:
				group.conditions?.map(conditionOrGroup => {
					if ('path' in conditionOrGroup) {
						// It's a condition
						return {
							path: conditionOrGroup.path,
							relation: conditionOrGroup.relation,
							value: this.prepareValue(conditionOrGroup.value, conditionOrGroup.relation)
						};
					}

					// It's a nested group
					return this.generateJSONObject(conditionOrGroup);
				}) || []
		};
	}

	onTabChange($event: MatTabChangeEvent) {
		if ($event.index === 1) {
			const jsonOutput = this.generateJSONObject(this.conditions);
			this.jsonValue = JSON.stringify(jsonOutput, null, 2);
		}
	}

	onUpdateJSONValue($event) {
		console.log('onUpdateJSONValue', $event);
		const data = (typeof $event === 'string' && $event.length > 0) ? JSON.parse($event) : $event;
		this.jsonValue = $event;
		this.isValid = this.validate(data);

		if (Object.keys(data).length) {
			this.conditions = this.prepareConditionData(data);
		}
	}

	validateItem(conditionOrGroup: ConditionGroup | Condition) {
		if ('path' in conditionOrGroup) {
			// It's a condition
			return !!conditionOrGroup.path;
		}

		if (conditionOrGroup?.type && ![ConditionType.OR, ConditionType.AND].includes(conditionOrGroup.type)) {
			return false;
		}

		for (const condition of conditionOrGroup.conditions) {
			if (!this.validateItem(condition)) {
				return false;
			}
		}

		return true;
	}

	validate(data) {
		if (!data) {
			return false;
		}

		if (Object.keys(data).length === 0) {
			return false;
		}

		if (data?.type && ![ConditionType.OR, ConditionType.AND].includes(data.type)) {
			return false;
		}

		if (!data.conditions) {
			return true;
		}

		for (const condition of data.conditions) {
			if (!this.validateItem(condition)) {
				return false;
			}
		}

		return true;
	}

	btnCopyToClipboardClick() {
		navigator.clipboard.writeText(this.jsonValue).then(() => {
			this.globalService.triggerSuccessMessage(
				$localize`:Visibility Conditions Copied to Clipboard Message Dialog@@visibilityConditionCopiedToClipboardDialogMessage:Data has been copied to clipboard.`
			);
		}).catch(err => {
			this.globalService.triggerErrorMessage(
				undefined, 
				$localize`:Copy to Clipboard Error Message Dialog@@copyToClipboardErrorDialogMessage:Failed to copy to clipboard`
			);
			console.error('Could not copy text to clipboard: ', err);
		});
	}

	btnPasteFromClipboardClick() {
		navigator.clipboard.readText().then(text => {
			text && this.onUpdateJSONValue(text);
		}).catch(err => {
			let message = $localize`:Read from Clipboard Error Message Dialog@@readFromClipboardErrorDialogMessage:Failed to read from clipboard.`;
			if (err.name === 'NotAllowedError') {
				message = $localize`:Clipboard Permission Denied Error Message Dialog@@clipboardPermissionDeniedErrorDialogMessage:Permission to access clipboard was denied.`
			}

			if (err.name === 'SyntaxError') {
				message = $localize`:Clipboard Syntax Error Message Dialog@@clipboardSyntaxErrorDialogMessage:Data from clipboard is not a valid JSON.`
			}

			this.globalService.triggerErrorMessage(
				undefined, 
				message
			);
			console.error('Could not read text from clipboard: ', err);
		});
	}

	save() {
		this.isValid = this.validate(this.conditions);

		if (!this.isValid) {
			this.dialog.open(MessageDialogComponent, {
				data: {
					message: $localize`:Visibility Conditions Incorrect Data Message Dialog@@visibilityConditionIncorrectDataDialogMessage:Visibility conditions are incorrect. Fill required data.`
				}
			});

			return;
		}

		console.log('save', this.conditions);
		this.dialogRef.close(this.conditions);
	}
}
